import {
  CSSProperties,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/hooks/useModal';
import {
  AlertBox,
  Button,
  CompareReportsModal,
  TariffsDetails,
  TariffsPaymentMethod,
  TariffsProDetails,
  TariffsProPlusDetails,
  TariffsPromoForm,
  TariffsRequestCustom,
} from '@/components';

import {
  ADDITION_MARK_TO_CATEGORY,
  getPlansAvailability,
  PlanCategoryEnum,
  TariffType,
  tariffDisplayData,
} from '@/config';
import { useAppSelector } from '@/redux/hooks';
import {
  paymentSelectors,
  profileSelectors,
  userSelectors,
} from '@/redux/selectors';
import { AccountModesEnum, Plan } from '@/api';
import { PaymentContext, PaymentContextProps } from '@/contexts';
import {
  getFormattedDateWithTime,
  getPrice,
  sortPlansByCategory,
} from '@/helpers';
import parse from 'html-react-parser';
import { useOpenChat, useDevice } from '@/hooks';
import { HubspotProvider } from '@aaronhayes/react-use-hubspot-form';
import { CompareTariffs } from '@/components/CompareTariffs';
import { TariffsSummary } from './components/TariffsSummary/TariffsSummary';
import { TariffsTabs } from './components/TariffsTabs/TariffsTabs';
import VerifiedCompanyOnly from './components/VerifiedCompanyOnly/VerifiedCompanyOnly';
import TariffEntry from './components/TariffEntry/TariffEntry';
import styles from './Tariffs.module.sass';
import { INDIVIDUAL_PRO_PLUS_PLAN } from './Tariffs.constants';

interface TariffsProps {}

const Tariffs: FC<TariffsProps> = () => {
  const [currentTariff, setCurrentTariff] = useState<Plan | null>(null);
  const [promo, setPromo] = useState('');
  const { t } = useTranslation();
  const location = useLocation();
  const { isMobile } = useDevice();
  const [activeCategory, setActiveCategory] = useState<PlanCategoryEnum | null>(
    null,
  );
  const [availableCategories, setAvailableCategories] = useState<
    PlanCategoryEnum[]
  >([]);

  const compareRef = useRef<HTMLDivElement>(null);
  const [isCompareExpanded, setIsCompareExpanded] = useState(false);
  const {
    paymentPlans,
    paymentLoading,
    discount: userDiscount,
    discountDueTime,
  } = useAppSelector(paymentSelectors.getPaymentSlice);

  const planCategories = useMemo<Record<string, Plan[]>>(() => {
    if (!paymentPlans?.plans) return {};
    return sortPlansByCategory(paymentPlans.plans);
  }, [paymentPlans?.plans]);

  const { data: userData } = useAppSelector(userSelectors.getUserSlice);
  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );
  const isAiTraceOnly = useAppSelector(userSelectors.checkIfAiTraceOnly);
  const { getBestDiscount, selectedOption, isDiscountWithExpirationTime } =
    useContext(PaymentContext) as PaymentContextProps;

  const setPromoAlertMsg = (promoCode: string) => {
    setPromo(promoCode);
  };

  const {
    isOpen: isOpenCompareReportsModal,
    onOpenModal: handleOpenCompareReportsModal,
    onCloseModal: handleCloseCompareReportsModal,
  } = useModal();

  const { handleOpenChat } = useOpenChat();

  useEffect(() => {
    if (profileData?.accountMode) {
      const { available, visible } = getPlansAvailability(
        profileData.accountMode,
        isAiTraceOnly,
      );

      setActiveCategory(() => available[0]);

      setAvailableCategories(() => [...available, ...visible]);
    }
  }, [profileData?.accountMode, isAiTraceOnly]);

  useEffect(() => {
    if (activeCategory && planCategories[activeCategory]) {
      const { planId } = location.state || { planId: null };
      const visiblePlans = planCategories[activeCategory];
      let currentPlan;

      if (
        !!currentTariff &&
        visiblePlans.find((plan) => plan.id === currentTariff.id)
      ) {
        currentPlan = currentTariff;
      } else {
        currentPlan = visiblePlans.find((plan) => plan.id === planId);
      }

      setCurrentTariff(currentPlan || visiblePlans[0]);
    }
  }, [location.state, planCategories, activeCategory]);

  const currentTariffPrices = useMemo(() => {
    if (!currentTariff) return;

    const { price, pricePerCheck, oldPrice, discount } = getPrice(
      currentTariff.price,
      currentTariff.value,
      currentTariff.old_price,
      getBestDiscount(Number(currentTariff.price), userDiscount),
    );

    return { price, pricePerCheck, oldPrice, discount };
  }, [paymentPlans, currentTariff, userDiscount, selectedOption]);

  const isPurchasable = useMemo(() => {
    if (currentTariff?.id === 'individual-pro-plus') {
      return false;
    }

    if (currentTariff) {
      const planType = ADDITION_MARK_TO_CATEGORY[currentTariff.addition];

      if (profileData?.accountMode) {
        return getPlansAvailability(
          profileData.accountMode,
          isAiTraceOnly,
        ).available.includes(planType);
      }
    }

    return false;
  }, [currentTariff, profileData?.accountMode, isAiTraceOnly]);

  const companyOnly = useMemo(() => {
    return activeCategory === PlanCategoryEnum.PRO_PLUS;
  }, [activeCategory]);

  const isPlaceholderCategory = useMemo(() => {
    if (
      activeCategory !== PlanCategoryEnum.PRO &&
      activeCategory !== PlanCategoryEnum.PRO_PLUS
    ) {
      return false;
    }
    if (profileData?.accountMode === AccountModesEnum.LITE) return true;
    return (planCategories[activeCategory]?.length ?? 0) === 0;
  }, [activeCategory, profileData?.accountMode, planCategories]);

  const hasTabs = useMemo(() => {
    return (availableCategories || []).length > 1;
  }, [availableCategories]);

  const requestUpgrade = useCallback(() => {
    if (currentTariff?.id === 'individual-pro-plus') {
      handleOpenChat();
      return;
    }

    handleOpenCompareReportsModal();
  }, [currentTariff?.id, handleOpenCompareReportsModal]);

  const handleComparePlans = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!isCompareExpanded) {
      setIsCompareExpanded(() => true);
    }

    setTimeout(() => {
      if (compareRef.current) {
        compareRef.current.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    }, 0);
  };

  const toggleComparePlans = () => {
    setIsCompareExpanded((prev) => !prev);
  };

  return (
    <HubspotProvider>
      <div
        className={styles.tariffs}
        style={
          {
            '--current-tariff-color': activeCategory
              ? tariffDisplayData[activeCategory].color
              : '',
          } as CSSProperties
        }
      >
        {profileData?.currentPlan && <TariffsSummary />}
        {!!activeCategory && hasTabs && (
          <div className={styles.nav}>
            <TariffsTabs
              value={activeCategory}
              className={styles.tabs}
              categories={availableCategories}
              onChange={setActiveCategory}
            />

            <button
              type="button"
              className={styles.comparePlans}
              onClick={handleComparePlans}
            >
              {t('translation.compare_plan_hint')}
            </button>
          </div>
        )}

        {isPlaceholderCategory && activeCategory === PlanCategoryEnum.PRO && (
          <TariffsProDetails />
        )}
        {isPlaceholderCategory &&
          activeCategory === PlanCategoryEnum.PRO_PLUS && (
            <TariffsProPlusDetails />
          )}

        {!isPlaceholderCategory && (
          <div className={styles.inner}>
            <div className={styles.body}>
              {companyOnly && <VerifiedCompanyOnly />}

              {isDiscountWithExpirationTime() && (
                <AlertBox type="info" className={styles.alert}>
                  {{
                    description: parse(
                      t('translation.discount_expire_str', {
                        date: getFormattedDateWithTime(discountDueTime * 1000),
                      }),
                    ),
                    icon: '⚠️',
                  }}
                </AlertBox>
              )}

              {!!activeCategory && (
                <div className={styles.items}>
                  {planCategories[activeCategory].map((item) => {
                    return (
                      <TariffEntry
                        key={item.id}
                        item={item}
                        planCategory={activeCategory}
                        isActive={item.id === currentTariff?.id}
                        onClick={() =>
                          !paymentLoading && setCurrentTariff(item)
                        }
                      />
                    );
                  })}

                  {activeCategory === PlanCategoryEnum.PRO_PLUS && (
                    <TariffEntry
                      key="individual-pro-plus"
                      item={INDIVIDUAL_PRO_PLUS_PLAN}
                      priceCustomText={t('translation.individual_price') || ''}
                      planCategory={activeCategory}
                      isActive={currentTariff?.id === 'individual-pro-plus'}
                      onClick={() =>
                        !paymentLoading &&
                        setCurrentTariff(INDIVIDUAL_PRO_PLUS_PLAN)
                      }
                    />
                  )}
                </div>
              )}
            </div>

            {currentTariff && currentTariffPrices && (
              <div className={styles.sidebar}>
                <TariffsDetails
                  tariff={currentTariff}
                  planCategory={activeCategory as TariffType}
                  tariffPrices={currentTariffPrices}
                  showPaymentTitle={isPurchasable}
                >
                  {isPurchasable && (
                    <TariffsPaymentMethod
                      planId={Number(currentTariff.id)}
                      tariffCost={Number(currentTariff.price)}
                      tariffType={currentTariff?.addition}
                    />
                  )}
                  {!isPurchasable && (
                    <Button
                      fullwidth
                      type="primary-black"
                      corner="rect"
                      className={styles.requestUpgradeButton}
                      onClick={requestUpgrade}
                    >
                      {t('translation.claim_plan_up')}
                    </Button>
                  )}
                </TariffsDetails>

                {isMobile && (
                  <>
                    {false && (
                      <TariffsPromoForm onSuccessfulSubmit={setPromoAlertMsg} />
                    )}
                    {false && <TariffsRequestCustom />}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div ref={compareRef}>
          <CompareTariffs
            isExpanded={isCompareExpanded}
            onToggle={toggleComparePlans}
          />
        </div>

        <CompareReportsModal
          isOpen={isOpenCompareReportsModal}
          onClose={handleCloseCompareReportsModal}
        />
      </div>

      <div id="hubspot-form" />
    </HubspotProvider>
  );
};

export default Tariffs;
